import { useRef, useState, type ChangeEvent } from 'react';
import { Color, Size, employeeReviewsPath, t, type EmployeeFileRow, type ReviewsSection } from '@bms2/shared';
import { Button } from '../../components/controls';
import { upload } from '../records/api';
import styles from './HrPages.module.css';

/** An open link's words: Open and the review's file name. */
const openText = (label: string): string => `${t('hr.reviews.open')} ${label}`;

/**
 * The Reviews section's controls (piece 34; decision 3185): HR opens each
 * signed review through the file proxy, and HR with the hire permission or a
 * manager above the job files a new one. 🔒 The review files on the job under
 * the seeded HR-class type, and the filing closes the job's anniversary To-Do.
 */
export function ReviewFiles({ apiBaseUrl, employmentId, rows, reviews, onFiled }: { apiBaseUrl: string; employmentId: string; rows: readonly EmployeeFileRow[]; reviews: ReviewsSection; onFiled: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'good' | 'error'; text: string } | null>(null);
  const openable = rows.filter((row) => row.fileId);
  const fileReview = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    event.target.value = '';
    if (!chosen) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', chosen, chosen.name);
    const answer = await upload<{ fileId: string; filename: string }>(employeeReviewsPath(employmentId), form, apiBaseUrl);
    setBusy(false);
    setNotice(answer.ok ? { tone: 'good', text: t('hr.reviews.filed') } : { tone: 'error', text: answer.message });
    if (answer.ok) onFiled();
  };
  if (openable.length === 0 && !reviews.mayFile && notice === null) return null;
  return (
    <div className={styles.fields} data-employee-reviews={employmentId}>
      {openable.length > 0 ? (
        <ul className={styles.list}>
          {openable.map((row) => (
            <li key={row.id} className={styles.item} data-review-open={row.fileId}>
              <a href={`${apiBaseUrl}/files/${row.fileId}/stream`} target="_blank" rel="noreferrer">{openText(row.label)}</a>
            </li>
          ))}
        </ul>
      ) : null}
      {reviews.mayFile ? (
        <div className={styles.controls}>
          <span data-review-file="open">
            <Button size={Size.Small} color={Color.Navy} text={[t('hr.reviews.file')]} actions={[() => input.current?.click()]} disabled={busy} />
          </span>
          <input ref={input} className={styles.hiddenFile} type="file" accept="application/pdf,image/*" aria-label={t('hr.reviews.file')} onChange={(event) => void fileReview(event)} data-review-input="true" />
          <p className={styles.meta}>{t('hr.reviews.hint')}</p>
        </div>
      ) : null}
      {notice ? <p className={styles.meta} role={notice.tone === 'error' ? 'alert' : 'status'} data-review-notice={notice.tone}>{notice.text}</p> : null}
    </div>
  );
}
